import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { startOfDayInTz, endOfDayInTz, formatInTz, formatDateInputInTz, DEFAULT_TIMEZONE } from '@/utils/timezone';
import { calculateConversionRate } from '@/utils/dateFieldStandards';
import { useVisibility } from '@/hooks/useVisibility';

interface PostcodeMetrics {
  postcode: string;
  suburb: string | null;
  state: string | null;
  metro_id: string | null;
  metro_name: string | null;
  totalJobs: number;
  totalRevenue: number;
  averageTicket: number;
  totalLeads: number;
  convertedLeads: number;
  lostLeads: number;
  conversionRate: number;
}

export const usePostcodeReport = (startDate: Date, endDate: Date, timezone: string = DEFAULT_TIMEZONE) => {
  const { canSeeCompanyFinancials } = useVisibility();
  return useQuery({
    enabled: canSeeCompanyFinancials,
    queryKey: ['reports', 'postcode-report', formatInTz(startDate, timezone, 'yyyy-MM-dd'), formatInTz(endDate, timezone, 'yyyy-MM-dd'), timezone],
    queryFn: async () => {
      // Use configured timezone for date boundaries
      const start = startOfDayInTz(startDate, timezone).toISOString();
      const end = endOfDayInTz(endDate, timezone).toISOString();
      const startDateStr = formatDateInputInTz(startDate, timezone);
      const endDateStr = formatDateInputInTz(endDate, timezone);

      // Fetch completed jobs by job_date for accurate revenue attribution
      const { data: jobs, error: jobsError } = await supabase
        .from('jobs')
        .select(`
          customer_postcode,
          customer_suburb,
          customer_state,
          metro_id,
          metros(name),
          invoice_total,
          revenue_before_gst,
          status,
          job_date
        `)
        .eq('status', 'completed')
        .gte('job_date', startDateStr)
        .lte('job_date', endDateStr);

      if (jobsError) throw jobsError;

      // Fetch leads within date range
      const { data: leads, error: leadsError } = await supabase
        .from('leads')
        .select(`
          customer_postcode, 
          customer_suburb, 
          customer_state, 
          metro_id,
          metros(name),
          status, 
          created_at
        `)
        .gte('created_at', start)
        .lte('created_at', end);

      if (leadsError) throw leadsError;

      // Group by postcode
      const postcodeMap = new Map<string, PostcodeMetrics>();

      // Process jobs
      jobs?.forEach((job: any) => {
        const postcode = job.customer_postcode || 'Unknown';

        if (!postcodeMap.has(postcode)) {
          postcodeMap.set(postcode, {
            postcode,
            suburb: job.customer_suburb,
            state: job.customer_state,
            metro_id: job.metro_id,
            metro_name: job.metros?.name || null,
            totalJobs: 0,
            totalRevenue: 0,
            averageTicket: 0,
            totalLeads: 0,
            convertedLeads: 0,
            lostLeads: 0,
            conversionRate: 0,
          });
        }

        const metrics = postcodeMap.get(postcode)!;
        metrics.totalJobs += 1;
        metrics.totalRevenue += Number(job.invoice_total || 0);
      });

      // Process leads
      leads?.forEach((lead: any) => {
        const postcode = lead.customer_postcode || 'Unknown';

        if (!postcodeMap.has(postcode)) {
          postcodeMap.set(postcode, {
            postcode,
            suburb: lead.customer_suburb,
            state: lead.customer_state,
            metro_id: lead.metro_id,
            metro_name: lead.metros?.name || null,
            totalJobs: 0,
            totalRevenue: 0,
            averageTicket: 0,
            totalLeads: 0,
            convertedLeads: 0,
            lostLeads: 0,
            conversionRate: 0,
          });
        }

        const metrics = postcodeMap.get(postcode)!;
        metrics.totalLeads += 1;

        // Track converted and lost leads for proper conversion rate
        if (lead.status === 'converted') {
          metrics.convertedLeads += 1;
        } else if (lead.status === 'lost') {
          metrics.lostLeads += 1;
        }
      });

      // Calculate derived metrics using standardized conversion rate
      const postcodeMetrics = Array.from(postcodeMap.values()).map((metrics) => {
        const averageTicket = metrics.totalJobs > 0
          ? metrics.totalRevenue / metrics.totalJobs
          : 0;

        // Use standardized conversion rate (excluding lost leads from denominator)
        const conversionRate = calculateConversionRate(
          metrics.convertedLeads,
          metrics.totalLeads
        );

        return {
          ...metrics,
          averageTicket,
          conversionRate,
        };
      });

      // Sort by revenue (highest first)
      return postcodeMetrics.sort((a, b) => b.totalRevenue - a.totalRevenue);
    },
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });
};
